import { z } from 'zod';

export const ClientModSchema = z
  .object({
    source: z.literal('modrinth'),
    projectId: z.string().regex(/^[A-Za-z0-9_-]{1,100}$/),
    slug: z.string().min(1).max(100),
    title: z.string().min(1).max(200),
    side: z.enum(['required', 'optional']),
    /** 編集画面に出すアイコン。保存するときに Modrinth の値で上書きする（ランチャーには渡さない） */
    iconUrl: z
      .string()
      .max(500)
      .regex(/^https:\/\//)
      .optional(),
  })
  .strict();
export const ClientModTemplateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    /** この版の Fabric 用に作る。サーバーは同じ版のテンプレートだけを選べる */
    mcVersion: z
      .string()
      .trim()
      .regex(/^[0-9][0-9A-Za-z.-]{0,31}$/, 'Minecraft の版を選んでください'),
    mods: z.array(ClientModSchema).max(100),
  })
  .strict()
  .refine((input) => new Set(input.mods.map((mod) => mod.projectId)).size === input.mods.length, {
    message: '同じ Mod は複数追加できません',
  });
export const ClientModTemplateSchema = z.object({
  revision: z.string().optional(),
  id: z.string().min(1).max(100),
  name: z.string(),
  /** 版を持たない以前のテンプレートは、これまでどおりどの版のサーバーでも使える */
  mcVersion: z.string().optional(),
  mods: z.array(ClientModSchema),
  /** この版で新しく作る Mod なしサーバーに、最初から選んでおく（1 つの版につき 1 つまで） */
  defaultForNew: z.boolean().optional(),
  /** 一覧で返すときだけ付く。このテンプレートを選んでいるサーバー */
  servers: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
});
export const ClientModTemplateDuplicateSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    mcVersion: z
      .string()
      .trim()
      .regex(/^[0-9][0-9A-Za-z.-]{0,31}$/, 'Minecraft の版を選んでください'),
    /** その版で使えない Mod を外して複製する。false なら使えない Mod があると断る */
    dropUnavailable: z.boolean().default(false),
  })
  .strict();
/** まとめて割り当てる。並べたサーバーにこのテンプレートを選び、ほかでこのテンプレートを選んでいたサーバーは「なし」に戻す */
export const ClientModTemplateAssignmentSchema = z
  .object({ serverIds: z.array(z.string().min(1).max(100)).max(200), defaultForNew: z.boolean() })
  .strict();
export type ClientModTemplateAssignment = z.infer<typeof ClientModTemplateAssignmentSchema>;
/** テンプレートをこのサーバーの版で使えるか。版を持たない以前のテンプレートはどの版でも使える */
export const clientTemplateFits = (template: { mcVersion?: string }, mcVersion: string) =>
  !template.mcVersion || template.mcVersion === mcVersion;
export const ClientTemplateSelectionSchema = z.object({ templateId: z.string().min(1).max(100).nullable() }).strict();
export const ClientTemplateStatusSchema = z.object({
  templateId: z.string().nullable(),
  clientTemplate: z.object({ name: z.string() }).optional(),
  loader: z.enum(['vanilla', 'fabric']),
  loaderVersion: z.string().optional(),
  unavailable: z.array(
    z.object({ projectId: z.string().optional(), title: z.string(), reason: z.string().optional() }),
  ),
  reason: z.string().optional(),
  /** hub 配下のサーバーなら hub の ID（クライアント Mod は hub のテンプレートを使い、ここでは選ばない） */
  hub: z.string().optional(),
});
export type ClientModTemplate = z.infer<typeof ClientModTemplateSchema>;
export type ClientModTemplateInput = z.infer<typeof ClientModTemplateInputSchema>;
export type ClientModTemplateDuplicate = z.input<typeof ClientModTemplateDuplicateSchema>;
export type ClientTemplateStatus = z.infer<typeof ClientTemplateStatusSchema>;

export const LauncherPackEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  mcVersion: z.string(),
  loader: z.enum(['fabric', 'neoforge', 'forge', 'vanilla']),
  kind: z.enum(['modded', 'vanilla']),
  clientTemplate: z.object({ name: z.string() }).optional(),
  host: z.string(),
  state: z.string(),
  port: z.number().int().min(1).max(65535),
  launcher: z.string().url(),
  online: z.object({ players: z.number().int().nonnegative(), max: z.number().int().nonnegative() }).nullable(),
  preparing: z.boolean().optional(),
  packwiz: z.string().url().optional(),
  mrpack: z.string().url().optional(),
  prism: z.string().url().optional(),
});
export type LauncherPackEntry = z.infer<typeof LauncherPackEntrySchema>;
